/*
 * ONBOARDING — the guided first session (docs/design/onboarding-tour.md §5).
 *
 * Orchestration only: the product does the teaching. This module owns the `onboarding` user
 * attr (one JSON string, inlined by main.html as `onboarding_attr`), shows the welcome card
 * and fills the learn tray with two unseen words of the self-rated level's band.
 * Everything in between — the rung ladder, the handoff hero, the drill — runs untouched, and
 * every explanation along the way belongs to the ONE glow walk (js/onboarding_guide.js).
 *
 * Two things this file used to do and no longer does (owner call 2026-08-11):
 *   • the two Driver.js spotlight beats. They were a second popup system beside the walk's
 *     own, with their own dismissal contract ("Got it") and their own overlay. The walk
 *     covers both moments — the tray/Start hero and the finish screen's garden bed — so
 *     Driver.js is retired, vendor files and all.
 *   • the signup nudge after the first finished drill. Nobody is asked to create an account
 *     on their first day; the account menu is where that offer lives.
 *
 * State machine: fresh → offered (welcome shown) → running (guided run live) → done | skipped.
 * The welcome card re-offers from fresh/offered AND from running (a tab closed mid-run left
 * nothing recoverable — next visit starts the offer over); never from done/skipped. A run LEFT
 * early through the walk's own doors (the quit card, the Dictionary door) is skipped, not running
 * (first-run R23, endedEarly). An ABSENT attr (every user predating the tour) means this file does
 * nothing, ever.
 *
 * A11y (spec §11): the cards are real modals on the msgbox chassis (PL.layers owns Esc +
 * outside-press, PL.focus hands focus back); Driver.js core has no focus management, so this
 * wrapper moves focus into each popover, returns it to a sensible control on close, announces
 * beats through an aria-live region, and pins animations off under prefers-reduced-motion.
 *
 * Dev trigger: ?tour=reset — rewrites the attr to fresh and re-runs the offer (spec §5).
 */
var ONBOARDING;

(function () {
	"use strict";

	var st = null;          // parsed onboarding JSON, or null = machinery disarmed

	function today() {
		return new Date().toISOString().slice(0, 10);
	}

	// The finished-LESSON markers (owner call 2026-10-05), beside the bare counters: `first` stamps
	// the day of the first finished session of each kind ("learn" | "drill") once ever, and
	// `lastDone` is the latest one — its kind, day and word count (null when the caller had none).
	// Never cleared by a replay: they record sessions, not the walk. The caller saves.
	function markSessionDone(kind, words) {
		if (!st.first) { st.first = {}; }
		if (!st.first[kind]) { st.first[kind] = today(); }
		st.lastDone = { kind: kind, ts: today(), words: typeof words === "number" ? words : null };
	}

	// SERIALIZED on purpose: two mutations in the same tick (the guided finish sets state
	// done, the tip engine's drill counter lands right behind it) used to fire two
	// concurrent POSTs updating the same user_attr row — Hibernate's optimistic lock
	// (updated_ts) made the loser a 500, and the error msgbox it raised swallowed the
	// finish beat's Esc. One POST in flight at a time; a save during flight marks dirty
	// and re-posts the FINAL st once, since every caller mutates this shared object.
	// Quiet on error (no msgbox): the attr is progress bookkeeping, never load-bearing.
	var saving = false, saveDirty = false;

	function save() {
		if (!st) { return; }
		if (saving) { saveDirty = true; return; }
		saving = true;
		$.ajax("user-attr-save", {
			type: "POST",
			data: { onboarding: JSON.stringify(st) },
			dataType: "json",
			complete: function () {
				saving = false;
				if (saveDirty) { saveDirty = false; save(); }
			}
		});
	}

	// ── Cards: the welcome card, on the msgbox chassis ───────────────────────────

	// Builds a modal card (overlay + box) and registers it with PL.layers, which owns Esc and
	// outside-press dismissal exactly like the msgbox. Returns { close(reason) }. The one-line
	// focus trap of msgbox.js grows into a cycle here — these cards hold several controls.
	function openCard(build, onClose) {
		var overlay = document.createElement("div");
		overlay.className = "msgOverlayClass";
		var box = document.createElement("div");
		box.className = "msgBoxClass obCardClass";
		box.setAttribute("role", "dialog");
		box.setAttribute("aria-modal", "true");
		overlay.appendChild(box);
		build(box);

		var focusToken = PL.focus.remember();
		document.body.appendChild(overlay);

		var layer = PL.layers.push({
			el: box,
			onClose: function () {
				overlay.remove();
				if (focusToken) { focusToken.restore({ ifWithin: box }); }
				if (onClose) { onClose(); }
				// Any card closing hands the stage back — re-run the beacons NOW. Declining
				// the welcome is the case that otherwise goes silent: state flips to skipped
				// with no repaint behind it, and the explore trail's first crumb must light
				// up on the decline click itself, not after some unrelated interaction.
				if (window.OB_TIPS) { OB_TIPS.evaluate(); }
			}
		});

		trapTab(box);
		return layer;
	}

	// Tab cycles inside the card — the page behind the backdrop is visible but must stay
	// unreachable (the msgbox rule, extended to more than one control). Shared with the
	// server-rendered guest card, which is the same DOM built by Thymeleaf instead of here.
	function trapTab(box) {
		box.addEventListener("keydown", function (e) {
			if (e.key !== "Tab") { return; }
			var items = box.querySelectorAll("button, a[href]");
			if (!items.length) { e.preventDefault(); return; }
			var first = items[0], last = items[items.length - 1];
			if (e.shiftKey && document.activeElement === first) {
				e.preventDefault(); last.focus();
			} else if (!e.shiftKey && document.activeElement === last) {
				e.preventDefault(); first.focus();
			}
		});
	}

	function el(tag, cls, text) {
		var node = document.createElement(tag);
		if (cls) { node.className = cls; }
		if (text != null) { node.textContent = text; }
		return node;
	}

	// A card's body text whose `{?}` is the help button itself (owner, 2026-10-08: "replace «?» with
	// the real image we use for the help button"): a small copy of its face, inline in the sentence.
	// Hovering it lights the real "?" in the tabs row, a click flashes it — it points, it does nothing
	// else (no menu: the card is still up, and the reader has not answered it).
	function helpText(cls, text) {
		var node = el("div", cls);
		var parts = String(text).split("{?}");
		for (var i = 0; i < parts.length; i++) {
			if (i > 0) { node.appendChild(helpRef()); }
			if (parts[i]) { node.appendChild(document.createTextNode(parts[i])); }
		}
		return node;
	}

	function helpRef() {
		var ref = el("span", "centralHeadPillClass obHelpRefClass", "?");
		ref.setAttribute("aria-hidden", "true");
		var flashTimer = null;
		function lit(on) {
			var real = document.getElementById("helpBookmarkId");
			if (real) { helpGlow(real, on, on ? 200 : 600); }
		}
		ref.addEventListener("mouseenter", function () { clearTimeout(flashTimer); lit(true); });
		ref.addEventListener("mouseleave", function () { clearTimeout(flashTimer); lit(false); });
		ref.addEventListener("click", function (e) {
			e.preventDefault();
			e.stopPropagation();
			clearTimeout(flashTimer);
			lit(true);
			flashTimer = setTimeout(function () { lit(false); }, 900);
		});
		return ref;
	}

	// ── The welcome card ──────────────────────────────────────────────────────────

	var LEVELS = ["A", "B", "C"];
	var LEVEL_KEYS = { A: "obLevelA_button", B: "obLevelB_button", C: "obLevelC_button" };

	function showWelcome() {
		if (st.state === "fresh") {
			st.state = "offered";
			st.offered_ts = today();
			save();
		}
		var chosen = st.level || "A";   // optional self-rating, default "I'm new" = A (§12.4)
		if (window.VISIT_LOG) { VISIT_LOG.mark("welcome", { level: chosen }); }

		var layer = openCard(function (box) {
			box.classList.add("obWelcomeCardClass");
			var top = el("div", "obWelcomeTopClass");
			var langs = welcomeLangRow();
			if (langs) { top.appendChild(langs); }
			var back = el("button", "pl-btn-quiet obWelcomeBackClass");
			back.type = "button";
			top.appendChild(back);
			wireWelcomeBack(back, false, null, function () { return busy; });   // `busy`: the primary's, below
			box.appendChild(top);
			box.appendChild(el("div", "msgBoxTitleClass", getRes("obWelcome_title")));
			box.appendChild(el("div", "msgBoxTextClass", getRes("obWelcome_body")));

			var levelRow = el("div", "obLevelRowClass");
			levelRow.appendChild(el("span", "obLevelLblClass", getRes("obLevelRow_label")));
			var group = el("div", "obLevelGroupClass");
			group.setAttribute("role", "radiogroup");
			group.setAttribute("aria-label", getRes("obLevel_label"));
			var buttons = {};
			LEVELS.forEach(function (lvl) {
				var b = el("button", "obLevelBtnClass", levelLabel(lvl));
				b.type = "button";
				b.setAttribute("role", "radio");
				buttons[lvl] = b;
				b.addEventListener("click", function () {
					chosen = lvl;
					if (window.VISIT_LOG) { VISIT_LOG.mark("level", { level: lvl }); }
					LEVELS.forEach(function (l) {
						buttons[l].setAttribute("aria-checked", String(l === lvl));
					});
				});
				group.appendChild(b);
			});
			LEVELS.forEach(function (l) {
				buttons[l].setAttribute("aria-checked", String(l === chosen));
			});
			levelRow.appendChild(group);
			box.appendChild(levelRow);

			var foot = el("div", "msgBoxFootClass obCardFootClass");
			var skip = el("button", "pl-btn-quiet obSkipBtnClass", getRes("obExplore_button"));
			skip.type = "button";
			skip.addEventListener("click", function () {
				st.state = "skipped";
				st.skipped_ts = today();
				save();
				walkBegins();
				// The walk opens on the (empty) Learn tab (owner spec 2026-07-27):
				// switch FIRST, so the guide sync fired by this card's onClose already
				// sees it and lights the Elementary header rather than the tab.
				if (window.openLearnTab) { window.openLearnTab(); }
				// BEFORE layer.close (owner call 2026-09-02, fixing two races the old order had):
				// endTour reads `box`'s geometry for the shrink-flight while the card is still
				// attached — a beat later, layer.close has already removed it — and it disarms
				// chapter 1's explore glow trail on the spot, so the close's own onClose
				// (OB_TIPS.evaluate → sync) never finds a decline with nothing yet telling it to
				// stay quiet and lights a Word List row for one frame before the sign-off can speak.
				// Declining IS an ending, and the reader who declines is exactly the one who has not
				// been told the tour can be started again (owner call 2026-09-01) — endTour says so,
				// flying this very card into the menu row that replays the walk, shrinking it along
				// the way (owner call 2026-09-02, spaced into a seven-beat choreography 2026-09-03,
				// its opening re-cut into one overlapping gesture 2026-09-04 — the card is CLONED on
				// the spot and flown from the next tick, so this close is free to run first).
				if (window.OB_GUIDE) { OB_GUIDE.endTour(box); }
				layer.close("api");
			});
			// Stacked, like the quit card's "continue" and the Word Lists offer's "yes": the
			// recommendation belongs ON the button it recommends (owner call 2026-08-13 — the
			// quit card said which answer it preferred and the welcome card, which asks the
			// same shape of question, did not).
			// Raised (obRaisedBtnClass): the first button a newcomer meets must look pressable at a
			// glance (owner ruling R13, docs/project/first-run-redesign.md). Same DOM as the guest
			// card in main.html, label span included — the face's arrow hangs off that span.
			var primary = el("button", "pl-btn-pill obPrimaryBtnClass obStackedBtnClass obRaisedBtnClass");
			primary.type = "button";
			primary.appendChild(el("span", "obPrimaryLblClass", getRes("obMeet_button")));
			var hint = el("span", "obBtnHintClass", getRes("obRecommended_hint"));
			primary.appendChild(hint);
			// The card STAYS UP until the first words are actually in hand (owner report
			// 2026-08-31: "it takes a noticeable amount of time … let's at least make some
			// progress icon"). It used to close on the press and only then send the request, so
			// the whole wait — one round trip, plus the Learn tab's rebuild behind it — was spent
			// on a screen that had not changed at all and had nothing left to press: a reader
			// with no way to tell a slow app from a lost click. Measured on dev the wait is small
			// (POST /loadTopUnseen ~60–80 ms warm, ~1.3 s for the first request after a restart,
			// which pays Spring's one-off warm-up), but "small" is not "invisible" and the button
			// is the one place feedback belongs.
			//
			// So the press turns the button into its own progress indicator — spinner, disabled,
			// the "(Recommended)" line swapped for "Fetching them…" — and the card closes when the
			// words land. A failure puts the button back rather than leaving a dead spinner: the
			// reader can press again, having already seen the app's own error message.
			var busy = false;
			primary.addEventListener("click", function () {
				if (busy) { return; }
				busy = true;
				primary.classList.add("obBtnBusyClass");
				primary.disabled = true;
				primary.setAttribute("aria-busy", "true");
				// The spinner rides in the HINT line, not beside the label: .obStackedBtnClass is a
				// flex COLUMN, so anything appended to the button itself would land on a third row.
				hint.textContent = "";
				hint.appendChild(el("span", "obBtnSpinnerClass"));
				hint.appendChild(el("span", null, getRes("obMeet_loading")));
				runBegins(chosen);
				startGuidedRun(chosen, function (ok) {
					if (ok) { layer.close("api"); return; }
					busy = false;
					primary.classList.remove("obBtnBusyClass");
					primary.disabled = false;
					primary.removeAttribute("aria-busy");
					hint.textContent = getRes("obRecommended_hint");   // drops the spinner with it
					primary.focus();
				});
			});
			foot.appendChild(skip);
			foot.appendChild(primary);
			box.appendChild(foot);

			// Focus starts on the primary — Enter takes the offered path (Esc still declines
			// softly: state stays offered and next visit asks again).
			setTimeout(function () { primary.focus(); }, 0);
		}, null);
		// Esc / outside press with no decision leaves the state at `offered` on purpose —
		// the card asks again next visit, and only the two buttons move the machine on.
	}

	// The reader-language row on a MINTED user's welcome card (owner ruling 2026-10-08): the
	// server-rendered row (main.html, #obWelcomeLangTplId — absent when the pair has one reader
	// language) cloned in, each pill switching langFrom exactly as the right pane's language pill
	// does — the attr, Rule R server-side, a plain reload. The card comes back by itself after it:
	// showWelcome has already left the state at `offered`, which is what raises it on a load.
	function welcomeLangRow() {
		var tpl = document.getElementById("obWelcomeLangTplId");
		if (!tpl || !tpl.content) { return null; }
		var row = tpl.content.firstElementChild.cloneNode(true);
		labelLangRow(row);
		row.querySelectorAll(".obWelcomeLangBtnClass").forEach(function (a) {
			a.addEventListener("click", function (e) {
				e.preventDefault();
				var code = a.getAttribute("data-lang");
				if (a.classList.contains("isOnClass") || row.getAttribute("aria-busy")) { return; }
				row.setAttribute("aria-busy", "true");   // one switch per card: the reload is coming
				if (window.VISIT_LOG) { VISIT_LOG.mark("welcomelang", { to: code }); }
				ajaxPostUserAttr("langFrom", code, function () { window.location.reload(); });
			});
		});
		return row;
	}

	function labelLangRow(row) {
		var label = getRes("obWelcomeLang_label");
		row.querySelector(".obWelcomeLangLblClass").textContent = label;
		row.querySelector(".obWelcomeLangRowClass").setAttribute("aria-label", label);
	}

	// A level pill's text. The first one names the language being learned (owner, 2026-10-08:
	// "Just starting to learn German", not "I'm new") — in the READER's language, from the same
	// lang_names map the walk's own sentences use (onboarding_guide.js learnedLanguage).
	function levelLabel(lvl) {
		var val = document.getElementById("langToValId");
		var shown = val ? val.textContent.trim() : "";
		var name = langDisplayName(val && val.getAttribute("data-lang-code"), shown);
		return strFormat(getRes(LEVEL_KEYS[lvl]), name);
	}

	// The card's top-right Back (owner, 2026-10-08): for the reader who picked the wrong language to
	// learn on the landing and wants to pick again. A navigation, not an answer — the state stays
	// `offered`, so a minted reader who comes back is asked again.
	//
	// It always goes to the READER-language landing (/ru), never back to the pair's (/ru/en) — owner,
	// 2026-10-08: a reader who came in on /ru/en and pressed Back landed on /ru/en again, a page with
	// one language to learn and nothing to change. /ru offers every language to learn:
	//
	//   • a GUEST gets this page's markers carried on (`carry`), so a re-pick still reaches the mint
	//     with the click id. Mints nothing (R8). (Until 2026-10-08 a guest went history.back() to the
	//     landing they came from — the very /ru/en that offered no other choice.)
	//   • a MINTED reader's /ru is rendered for a visitor the server knows, so its buttons carry
	//     `pick=1`, and a pick there re-points the reader's pair the way the right-panel pill does
	//     (PageController.guestApp, owner 2026-10-08) — then lands back here, state still `offered`,
	//     on the welcome card of the pair they picked.
	function wireWelcomeBack(btn, guest, carry, busy) {
		btn.textContent = getRes("obBack_button");
		btn.setAttribute("title", getRes("obWelcomeBack_tooltip"));
		btn.setAttribute("data-vl", "welcome-back");
		btn.addEventListener("click", function () {
			if (busy && busy()) { return; }
			if (window.VISIT_LOG) { VISIT_LOG.mark("welcomeback", { guest: !!guest }); }
			navigate(readerLanding() + (guest && carry ? carry() : ""));
		});
	}

	// The landing of the language on screen — /ru. The page's address names the language it is read
	// in, so its selector marks the right pill whatever the browser says; its pair buttons offer
	// every language to learn, and its reader pills every other language to read in.
	function readerLanding() {
		return "/" + ($("#langFromValId").attr("data-lang-code") || "").toLowerCase();
	}

	function navigate(url) {
		if (window.VISIT_LOG) { VISIT_LOG.go(url, "welcomeback"); } else { window.location.assign(url); }
	}

	// The walk's Back history starts at the welcome card's answer (onboarding_guide.js runStarted):
	// its first popup is the one whose Back returns here.
	function walkBegins() {
		if (window.OB_GUIDE && OB_GUIDE.runStarted) { OB_GUIDE.runStarted(); }
	}

	// Either welcome card's "yes": the guided run begins — or begins AGAIN, since a reload mid-run
	// re-offers the card (state `running`) and accepting it fetches two NEW words. The entry
	// chapter starts over with them, as replay() starts it over: its cursor left where the old
	// words had it opened "you're on your own now" over a new, unflipped first card, and a flip
	// that showed nothing (tour-exhaustive-test.md D2). Nothing else is reset — `running` is
	// chapter 1's state alone (the finish screen moves it on), so no later chapter has a cursor yet.
	function runBegins(chosen) {
		st.state = "running";
		st.level = chosen;
		st.run_ts = today();
		st.meet = null;
		save();
		walkBegins();
	}

	// ── The GUEST card ────────────────────────────────────────────────────────────
	//
	// docs/project/entry-flow-redesign.md R5. On /<locale>/<learn>/app there is no user at all, so
	// `onboarding_attr` is null, `st` is null and everything above stands down — this is the whole
	// of the tour's entry for that page. Three differences from showWelcome, each of them the
	// point rather than an accident:
	//
	//   • The card is SERVER-RENDERED (main.html, trap T4) and this only FILLS and WIRES it. A
	//     JS-built card appears ~350 ms after load, and until then the "?" and the app behind it
	//     are clickable, which is what R6 forbids.
	//   • ⛔ NO Escape, NO outside-close, NO close button. The minted card's soft decline leaves
	//     the state at `offered` and asks again next visit; a guest has nowhere to leave a state
	//     and no next visit to be asked on. The two buttons are the only ANSWERS on this card; the
	//     top-right Back (2026-10-08) leaves it as a navigation, minting nothing.
	//   • Each button MINTS FIRST — one XHR to POST /start — and then runs today's handler
	//     unchanged. That is the entire difference between the guest path and the minted one.
	function showGuestWelcome() {
		var overlay = document.getElementById("obGuestCardId");
		if (!overlay) { return; }               // not the guest page after all: nothing to do
		var box = overlay.querySelector(".obCardClass");
		var chosen = "A";                       // the optional self-rating, "I'm new" (§12.4)
		if (window.VISIT_LOG) { VISIT_LOG.mark("welcome", { level: chosen, guest: true }); }

		box.querySelector(".msgBoxTitleClass").textContent = getRes("obWelcome_title");
		box.querySelector(".msgBoxTextClass").textContent  = getRes("obWelcome_body");

		box.querySelector(".obLevelLblClass").textContent = getRes("obLevelRow_label");
		var group = box.querySelector(".obLevelGroupClass");
		group.setAttribute("aria-label", getRes("obLevel_label"));
		var buttons = {};
		LEVELS.forEach(function (lvl) {
			var b = group.querySelector("[data-level='" + lvl + "']");
			buttons[lvl] = b;
			b.textContent = levelLabel(lvl);
			b.addEventListener("click", function () {
				chosen = lvl;
				if (window.VISIT_LOG) { VISIT_LOG.mark("level", { level: lvl, guest: true }); }
				LEVELS.forEach(function (l) {
					buttons[l].setAttribute("aria-checked", String(l === lvl));
				});
			});
		});
		LEVELS.forEach(function (l) {
			buttons[l].setAttribute("aria-checked", String(l === chosen));
		});

		var skip    = box.querySelector(".obSkipBtnClass");
		var primary = box.querySelector(".obPrimaryBtnClass");
		var label   = primary.querySelector(".obPrimaryLblClass");
		var hint    = primary.querySelector(".obBtnHintClass");
		skip.textContent  = getRes("obExplore_button");
		label.textContent = getRes("obMeet_button");
		hint.textContent  = getRes("obRecommended_hint");

		var focusToken = PL.focus.remember();
		var layer = PL.layers.push({
			el: box,
			// ⛔ The two "no"s, both through the chassis rather than around it: onEsc returning
			// true means the manager does NOT close on Escape, and an entry that reports itself
			// permanently suspended is never closed by an outside pointerdown either.
			onEsc: function () { return true; },
			suspended: function () { return true; },
			onClose: function () {
				overlay.remove();
				if (focusToken) { focusToken.restore({ ifWithin: box }); }
				if (window.OB_TIPS) { OB_TIPS.evaluate(); }
			}
		});
		trapTab(box);

		var busy = false;
		function lock(on) {
			busy = on;
			skip.disabled = on;
			primary.disabled = on;
		}

		// Which link the visitor arrived by (docs/project/ad-attribution.md): the welcome page's
		// CTA carried utm_campaign and the ad platform's click id onto THIS page's address, and
		// the mint below is where they are stamped on the user row. They ride on the QUERY of the
		// POST — the one place the server reads a marker on every page — and only the names the
		// server knows (Attribution.java SOURCE_PARAM / CAMPAIGN_PARAM / CLICK_ID_PARAMS, and
		// landing_lang.js; utm_source so the stamp can say which platform, §9.3) travel; nothing
		// else of the address does. The rest of an ad click was kept once, at the landing
		// (the ad_click row, ad-attribution.md §9).
		// An AI assistant that linked straight to this page with only its Referer has no marker on
		// the address; the server read it and put it on <html data-arrival> (§10). The address wins.
		function attributionQuery() {
			var here = new URLSearchParams(window.location.search);
			var arrival = new URLSearchParams(document.documentElement.getAttribute("data-arrival") || "");
			var out = new URLSearchParams();
			["utm_source", "utm_campaign", "gclid", "gbraid", "wbraid", "msclkid", "rdt_cid"].forEach(function (name) {
				var value = here.get(name) || arrival.get(name);
				if (value) { out.set(name, value); }
			});
			var query = out.toString();
			return query ? "?" + query : "";
		}

		// The reader-language row (owner ruling 2026-10-08), server-rendered as plain links to this
		// pair's guest page in each language — the switch IS the navigation, and nothing is minted
		// (R8). Each link carries this page's arrival markers on, for the same reason the mint's POST
		// does: the page they lead to is where the mint will read them, and an ad visitor who
		// changes language first must not arrive there as a visitor from nowhere.
		// The third way off this card, and not a decision: a plain navigation back (see wireWelcomeBack).
		wireWelcomeBack(box.querySelector(".obWelcomeBackClass"), true, attributionQuery,
			function () { return busy; });

		var langRow = box.querySelector(".obWelcomeLangClass");
		if (langRow) {
			labelLangRow(langRow);
			var carry = attributionQuery();
			langRow.querySelectorAll(".obWelcomeLangBtnClass").forEach(function (a) {
				if (carry) { a.setAttribute("href", a.getAttribute("href") + carry); }
				a.addEventListener("click", function (e) {
					// The current language is not a switch, and a mint in flight is not to be walked
					// away from by a reload of this same card.
					if (busy || a.classList.contains("isOnClass")) { e.preventDefault(); return; }
					if (window.VISIT_LOG) { VISIT_LOG.mark("welcomelang", { to: a.getAttribute("data-lang"), guest: true }); }
				});
			});
		}

		// The ONE place on this page that creates a user (R5). `done` runs with `st` already
		// armed and guided mode on, i.e. in exactly the state a minted user's page loads in — so
		// each caller below is today's handler, unchanged.
		function mint(choice, done, fail) {
			// The iPad the User-Agent cannot see (main.html's pointer probe marked it). A real
			// phone or tablet is caught server-side by DeviceGate and comes back as a redirect
			// below; this is the half no server gate can answer. Either way nothing is minted.
			if (window.handheld_probe) {
				if (window.VISIT_LOG) { VISIT_LOG.mark("start", { choice: choice, level: chosen, out: "desktop" }); VISIT_LOG.go("/desktop", "probe"); return; }
				window.location.assign("/desktop");
				return;
			}
			$.ajax("/start" + attributionQuery(), {
				type: "POST",
				headers: { "X-Requested-With": "XMLHttpRequest" },
				data: {
					langFrom: $("#langFromValId").attr("data-lang-code"),
					langTo:   $("#langToValId").attr("data-lang-code"),
					choice:   choice,
					level:    chosen
				},
				dataType: "json"
			}).done(function (data) {
				// A handheld, or a visitor who turned out to have an identity after all (a second
				// tab signed in while this page sat open): the server says where to go instead.
				if (data && data.redirect) {
					if (window.VISIT_LOG) { VISIT_LOG.mark("start", { choice: choice, level: chosen, out: "redirect" }); VISIT_LOG.go(data.redirect, "server"); return; }
					window.location.assign(data.redirect);
					return;
				}
				if (window.VISIT_LOG) { VISIT_LOG.mark("start", { choice: choice, level: chosen, out: "minted" }); }
				st = { v: 1, state: "fresh" };
				// ⭐ THE PAGE IS NO LONGER A GUEST'S, and nothing reloads it. Every load-time
				// request finished before this XHR ran, each with its guest shape, so anything
				// that was empty BECAUSE there was no user stays empty until something asks
				// again (owner report 2026-09-09: "why no word lists here"). Two things follow:
				//
				// 1. The flag itself — read by login_main.js and by main.html's pointer probe.
				//    Everything downstream of this moment must behave as it does for a user.
				// 2. The Word Lists panel, the one pane whose guest answer is a LITERAL empty
				//    list: the system lists (My List, the level lists, the garden, Topics) are
				//    per-user rows, created on the first wordlist-load a user makes — and this
				//    call is now that first one. The tray and the progress panel need no such
				//    nudge (a brand-new user's really are empty and zero), and the tips payload
				//    was already the real one at render.
				window.guest_mode = false;
				if (window.plWlPanel && plWlPanel.reload) { plWlPanel.reload(); }
				tipsApi.touchDay();      // the day counter a normal page load would have bumped
				// guided MODE arms the walk; the mint set the attr server-side, but this page was
				// rendered before there was a user to render it for, so the body class is off.
				// Same call replay() makes, for the same reason.
				PL.mode.setGuided(true);
				done();
			}).fail(function (xhr) {
				if (window.VISIT_LOG) { VISIT_LOG.mark("start", { choice: choice, level: chosen, out: "failed", status: xhr && xhr.status }); }
				showMessage(getRes("operationCannotComplete_message"));
				if (fail) { fail(); }
			});
		}

		skip.addEventListener("click", function () {
			if (busy) { return; }
			lock(true);
			mint("explore", function () {
				st.state = "skipped";
				st.skipped_ts = today();
				save();
				walkBegins();
				// The same order as the minted card's decline, and the order matters there for
				// two races the comment on showWelcome's skip button spells out: switch tab
				// first, then endTour while `box` is still attached, then close.
				if (window.openLearnTab) { window.openLearnTab(); }
				if (window.OB_GUIDE) { OB_GUIDE.endTour(box); }
				layer.close("api");
			}, function () { lock(false); });
		});

		function resetPrimary() {
			lock(false);
			primary.classList.remove("obBtnBusyClass");
			primary.removeAttribute("aria-busy");
			hint.textContent = getRes("obRecommended_hint");   // drops the spinner with it
			primary.focus();
		}

		primary.addEventListener("click", function () {
			if (busy) { return; }
			lock(true);
			// The card stays up, spinner running, until the first words are in hand — the same
			// affordance the minted card grew on 2026-08-31, and it covers MORE here: the wait is
			// now the mint plus the fetch.
			primary.classList.add("obBtnBusyClass");
			primary.setAttribute("aria-busy", "true");
			hint.textContent = "";
			hint.appendChild(el("span", "obBtnSpinnerClass"));
			hint.appendChild(el("span", null, getRes("obMeet_loading")));
			mint("meet", function () {
				runBegins(chosen);
				startGuidedRun(chosen, function (ok) {
					if (ok) { layer.close("api"); return; }
					resetPrimary();
				});
			}, resetPrimary);
		});

		setTimeout(function () { primary.focus(); }, 0);
	}

	// ── The guided run ────────────────────────────────────────────────────────────

	// `done(ok)` is the welcome card's progress affordance talking: the card holds itself open,
	// spinner running, until this says the words are in hand (or that they are not coming). It is
	// called EXACTLY once on every path — the empty batch that used to `return` quietly, and every
	// transport failure, both of which would otherwise strand a spinner. Optional: nothing else
	// calls this.
	function startGuidedRun(level, done) {
		var settled = false;
		function finish(ok) {
			if (settled) { return; }
			settled = true;
			if (done) { done(ok); }
		}
		ajaxPost("loadTopUnseen", {
			termLang: $("#langToValId").attr("data-lang-code"),
			defLang: $("#langFromValId").attr("data-lang-code"),
			level: level,
			// TWO (owner ruling R11, docs/project/first-run-redesign.md, 2026-10-07): the guided
			// first session meets a couple of words, two parts of speech at random — ruled after
			// the 10-07 read, where half the guided visitors stopped inside the first card. It
			// was 3 since 2026-08-08 (5 before that). The explore walk's Random preset is not
			// this path and still offers 3. Every string of the guided run says "a couple of
			// words", never a number, so nothing else counts with it.
			number: 2
		}, function (data) {
			var defs = (data && data.definitions) || [];
			if (!defs.length) {
				showMessage(getRes("noWords_message"));
				finish(false);
				return;
			}
			// The band the words came from (LEVEL_<band> in the Word Lists pane), stamped on the
			// run: the tour's "add a few more" step (onboarding_guide.js chosenLevelHeader) opens
			// that list, so the next handful is the same level as these two words — not the
			// easiest list whatever was chosen on the welcome card (owner, 2026-10-07). Saved at
			// once: a reload before the next save would otherwise lose it, and the guide would
			// fall back to the first list. Absent from an older server's answer = no stamp.
			if (data.band) { st.band = data.band; save(); }
			LRN.addWords(defs, false, true);   // supersede the (empty) learn tray
			// The card goes FIRST, before the tab rebuild it was hiding: openLearnTab is a full
			// chrome teardown behind a modal backdrop, and closing after it would show the reader
			// a frozen spinner over the very repaint it was waiting for.
			finish(true);
			window.openLearnTab();
			// The tray + Start band paint synchronously above; the glow walk picks the hero
			// up from here (its `start` step) — the timeout only lets layout settle so the
			// halo measures the final geometry.
			setTimeout(function () {
				if (window.OB_TIPS) { OB_TIPS.evaluate(); }
			}, 400);
		}, getRes("operationCannotComplete_message"),
		// No `failure` handler on purpose — supplying one REPLACES ajaxPost's whole error path,
		// including the `redirect` branch that sends an expired session back to the login page.
		// `complete` runs after that path instead, on success and failure alike, so the button is
		// handed back whatever happened and `settled` keeps the success case single.
		null,
		function () { finish(false); });
	}

	// ── The practice finish: chapter 1 ends, the tour takes over ──────────────────

	// Called from main.js's session-end hook once the finish screen is fully painted (bed
	// included). Two things happen here and their ORDER matters: the guide is told first,
	// while it can still see that its walk was live, and only then does the guided RUN's
	// state machine reach `done`.
	//
	// The signup nudge that used to follow the bed beat is GONE (owner call 2026-08-11):
	// nobody is asked to create an account on their first day. Signing up stays available
	// in the account menu, where the user goes when they want it.
	function finishScreenShown() {
		if (!st) { return; }
		if (window.OB_GUIDE) { OB_GUIDE.practiceFinished(); }
		if (st.state === "running") {
			st.state = "done";
			st.done_ts = today();
			save();
		}
		// The tour's first step lives on this very screen (the garden bed), and nothing else
		// repaints behind it — evaluate once the summary sections have settled.
		setTimeout(function () {
			if (window.OB_TIPS) { OB_TIPS.evaluate(); }
		}, 600);
	}

	// ── "Are you trying to leave?" — the walk's one way out ───────────────────────
	//
	// Raised by onboarding_guide.js after the fourth press the wall swallowed. The two
	// buttons are deliberately lopsided (owner spec 2026-08-11): staying is the big pill on
	// the right where the eye lands, leaving is the quiet one on the left. Esc, the overlay
	// and anything short of pressing "yes" all mean stay — a mis-click must never end the
	// tour, which is the very thing the wall exists to prevent.
	// A third answer since 2026-10-08, when `onStartOver` is given: "Start over" — the replay the
	// walk's Back on its first popup also leads to, here from anywhere in the walk so that Esc or two
	// stray presses reach it in one press. The two quiet answers stack on the left; staying keeps the right.
	function tourQuitCard(onQuit, onStay, onStartOver) {
		var decided = false;
		var layer = openCard(function (box) {
			box.appendChild(el("div", "msgBoxTitleClass", getRes("obTourQuit_title")));
			box.appendChild(helpText("msgBoxTextClass", getRes("obTourQuit_body")));
			var foot = el("div", "msgBoxFootClass obCardFootClass");
			var quiet = el("div", "obCardFootQuietClass");
			var quit = el("button", "pl-btn-quiet obSkipBtnClass", getRes("obTourQuitYes_button"));
			quit.type = "button";
			quit.addEventListener("click", function () {
				decided = true;
				// BEFORE layer.close (owner call 2026-09-02): onQuit shrink-flies `box` to the
				// "?" — a beat later the card is gone with the close.
				if (onQuit) { onQuit(box); }
				layer.close("api");
			});
			// Two lines inside the one pill: the answer, then a smaller "(Recommended)"
			// under it. The nudge belongs ON the button it recommends — as body copy it
			// would be one more sentence to read before the reader can see the choice.
			var stay = el("button", "pl-btn-pill obPrimaryBtnClass obStackedBtnClass");
			stay.type = "button";
			stay.appendChild(el("span", null, getRes("obTourQuitNo_button")));
			stay.appendChild(el("span", "obBtnHintClass", getRes("obRecommended_hint")));
			stay.addEventListener("click", function () { layer.close("api"); });
			quiet.appendChild(quit);
			if (onStartOver) {
				var over = el("button", "pl-btn-quiet obSkipBtnClass obRestartBtnClass", getRes("obTourQuitRestart_button"));
				over.type = "button";
				over.addEventListener("click", function () {
					decided = true;
					// AFTER layer.close: the replay opens the welcome card, and a card pushed while this
					// one is still the top layer would be the one its close takes down.
					layer.close("api");
					onStartOver();
				});
				quiet.appendChild(over);
			}
			foot.appendChild(quiet);
			foot.appendChild(stay);
			box.appendChild(foot);
			setTimeout(function () { stay.focus(); }, 0);
		}, function () {
			if (!decided && onStay) { onStay(); }
		});
	}

	// ── The tour's two doors — the Practice and Dictionary tabs (first-run R16) ────
	//
	// Raised by onboarding_guide.js (openDoor) when a reader in the Learn chapter presses one of the
	// two mode tabs the walk is not leading to. Owner's design and copy (R16, mockup
	// temp/first-run-clickable-v2.html): Practice — "go straight to Practice? the tour will continue
	// there" (the two words go along, R21); Dictionary — "this ends the tour; the '?' menu starts it
	// again". Same chassis and the same pair as the quit card, the other way round: the reader
	// pressed the tab, so the way they were already heading is the pill on the right and focused,
	// and "stay" is the quiet answer. Esc and the backdrop read as stay, exactly as on the quit
	// card — a mis-press must never move anyone.
	var DOOR_KEYS = {
		practice: { title: "obDoorPractice_title", body: "obDoorPractice_body",
			go: "obDoorPracticeGo_button", stay: "obDoorPracticeStay_button" },
		dict: { title: "obDoorDict_title", body: "obDoorDict_body",
			go: "obDoorDictGo_button", stay: "obDoorDictStay_button" }
	};

	function tourDoorCard(kind, onGo, onStay) {
		var keys = DOOR_KEYS[kind];
		if (!keys) { return; }
		var decided = false;
		var layer = openCard(function (box) {
			box.classList.add("obDoorCardClass");
			box.setAttribute("data-door", kind);
			box.appendChild(el("div", "msgBoxTitleClass", getRes(keys.title)));
			box.appendChild(helpText("msgBoxTextClass", getRes(keys.body)));
			var foot = el("div", "msgBoxFootClass obCardFootClass");
			var stay = el("button", "pl-btn-quiet obSkipBtnClass", getRes(keys.stay));
			stay.type = "button";
			stay.addEventListener("click", function () { layer.close("api"); });
			var go = el("button", "pl-btn-pill obPrimaryBtnClass", getRes(keys.go));
			go.type = "button";
			go.addEventListener("click", function () {
				decided = true;
				// BEFORE layer.close: the Dictionary door ends the walk, and endTour shrink-flies `box`
				// into the "?" — a beat later the card is gone with the close (the quit card's order).
				if (onGo) { onGo(box); }
				layer.close("api");
			});
			foot.appendChild(stay);
			foot.appendChild(go);
			box.appendChild(foot);
			setTimeout(function () { go.focus(); }, 0);
		}, function () {
			if (!decided && onStay) { onStay(); }
		});
	}

	// ── "Now you are ready" — the handover into the Word Lists chapter ────────────
	//
	// Raised by onboarding_guide.js the moment the dictionary chapter's last step lands (the
	// tray is full and Start Learning is one press away). Owner spec 2026-08-13: say the loop
	// is complete, then OFFER the next chapter rather than starting it — the reader who wants
	// to go and learn must be able to, and the one who says yes gets a walk through the panel
	// they have only been shown two rows of.
	//
	// The pair is lopsided the same way the quit card's is, and for the same reason: the
	// recommended answer is the big pill on the right, carrying its own "(Recommended)" line.
	// Esc and the overlay are read as the quiet answer — the walk ends and the reader is left
	// where they wanted to be, in front of a full tray.
	function tourContinueCard(onYes, onNo) {
		var decided = false;
		var layer = openCard(function (box) {
			box.appendChild(el("div", "msgBoxTitleClass", getRes("obWlOffer_title")));
			box.appendChild(helpText("msgBoxTextClass", getRes("obWlOffer_body")));
			var foot = el("div", "msgBoxFootClass obCardFootClass");
			var no = el("button", "pl-btn-quiet obSkipBtnClass", getRes("obWlOfferNo_button"));
			no.type = "button";
			no.addEventListener("click", function () {
				decided = true;
				// BEFORE layer.close (owner call 2026-09-02): onNo shrink-flies `box` to the
				// "?" — a beat later the card is gone with the close.
				if (onNo) { onNo(box); }
				layer.close("api");
			});
			var yes = el("button", "pl-btn-pill obPrimaryBtnClass obStackedBtnClass");
			yes.type = "button";
			yes.appendChild(el("span", null, getRes("obWlOfferYes_button")));
			yes.appendChild(el("span", "obBtnHintClass", getRes("obRecommended_hint")));
			yes.addEventListener("click", function () {
				decided = true;
				layer.close("api");
				if (onYes) { onYes(); }
			});
			foot.appendChild(no);
			foot.appendChild(yes);
			box.appendChild(foot);
			setTimeout(function () { yes.focus(); }, 0);
		}, function () {
			if (!decided && onNo) { onNo(); }   // Esc / the overlay: read as a decline
		});
	}

	// ── The help hub — the "?" at the right end of the tabs row (spec §7) ─────────
	// Three doors and one setting, in the order the owner set on 2026-09-01: the guided tour, the
	// word-list tour, the welcome screen, the large-tooltips switch — then, under a rule, the two
	// tip-of-the-day doors, which are about the app's CONTENT rather than about the app.
	//
	// "How it works" was the fifth door until that day and is gone entirely, card and copy
	// (owner: "remove and delete from the app, it's useless now"). It was a five-line paragraph
	// summary of five surfaces the guided tour walks the reader through by hand.
	//
	// (No keyboard-shortcut card: each shortcut lives in the tooltip of the button it
	// duplicates.) Wired for EVERY user, not just tour users — the hub
	// is how a user predating the tour opts in: replay() seeds the attr fresh, and
	// from there the whole machinery (welcome card, beats, beacons) arms itself.

	var hubLayer = null;   // the open menu's PL.layers handle (null = closed)
	var hubTimer = null;   // …and its auto-close, for the one caller that asks for one

	function replay() {
		// Blank slate first (owner spec 2026-07-27): both staging trays are emptied so the
		// replayed walk starts where a first visit starts — the guided run's first words land
		// in a clean tray, and the "I'll explore on my own" path opens an EMPTY Learn tab
		// (its handler switches tabs; this is what makes that tab empty). Word lists survive:
		// they are the user's library, not tour state.
		if (window.resetTraysForReplay) { window.resetTraysForReplay(); }
		if (!st) {
			st = { v: 1, state: "fresh" };   // the pre-tour user's opt-in moment
		} else if (st.state !== "fresh" && st.state !== "offered") {
			// done/skipped/running alike return to `offered`: the welcome card asks
			// again, and only its two buttons move the machine on (same soft-decline
			// contract as the first visit). Progress bookkeeping (tips, drills, days)
			// survives — a replay re-runs the tour, it does not forget what was seen.
			st.state = "offered";
			st.offered_ts = today();
		}
		// Re-arm the explore glow guide for THIS replay: it runs until a drill is
		// finished, and without moving that mark a user who has already drilled once
		// declined into a silent app (owner report 2026-08-08). The counter itself is
		// left alone — the milestone tips read it — only the "since when" moves.
		st.guideFrom = st.drills || 0;
		// Same mark for the 3-word first scoop: a replayed walk offers the gentle 3
		// again until ITS first learn session completes, then back to 5.
		st.learnsFrom = st.learns || 0;
		// …and the tour is un-run: a replay repeats the whole thing, dictionary chapter
		// included. The first finished drill of this run sets the cursor again.
		st.tour = null;
		// Same for the entry chapter, which is otherwise once-ever: the first flipped meet card
		// of the replayed run opens it again.
		st.meet = null;
		// …and the Word Lists chapter, offer included: a replay re-asks it.
		st.wl = null;
		// …and the sign-off, which is the walk's last word and belongs to whichever ending the
		// REPLAYED walk reaches.
		st.sign = null;
		// …and the early-exit and walked-to-the-end markers, which belong to the walk they described.
		// (The finished-session markers `first`/`lastDone` stay: they count sessions, not walks.)
		st.ended = null;
		st.finished = null;
		save();
		// Re-entering the tour IS asking to be guided (tooltip-engine-consolidation.md §2.3):
		// guided MODE arms the walk, so a replay with the mode off would seed the state and
		// then show no cues at all. Stamping it on here is also the one place the mode is
		// written by something other than its own toggle — through the same single writer.
		PL.mode.setGuided(true);
		showWelcome();
	}

	// …and the NARROWEST door of all: the Word Lists walk on its own (owner call 2026-08-13).
	// `replay` re-runs everything — welcome card, the first words, a drill, the dictionary
	// chapter — and the panel walk only arrives at the end of it, behind a card. That is a lot to
	// sit through for a reader who wants the one chapter about the surface they use every day.
	//
	// Deliberately NOT a replay: no tray is emptied, no cursor but chapter 3's is touched, and the
	// welcome card is not shown. The two things it does share with replay() are the two the walk
	// cannot run without — guided MODE on (it is the single switch that arms every cue) and an
	// attr to keep the cursor in. A user predating the tour has none, so one is seeded HERE, as
	// `done`: state `fresh`/`offered` would raise the welcome card on their next load, and
	// `skipped`/`running` would arm chapter 1's explore glow behind this walk's back. They asked
	// for one chapter; they get one chapter.
	function startWordListTour() {
		if (!st) {
			st = { v: 1, state: "done", tour: -1, meet: -1 };
			save();
		}
		PL.mode.setGuided(true);
		if (window.OB_GUIDE) { OB_GUIDE.startWordLists(); }
	}

	// The landing page, on demand. /welcome renders it regardless of session (the `/`
	// rule only shows it to user-less requests), so this is a plain navigation — no
	// state touched: the landing's CTA just updates langTo and comes back.
	function showWelcomeScreen() {
		window.location.href = "/welcome";
	}

	// `name` is the row's handle for the guided walk (onboarding_guide.js hubRow) — the sign-off
	// chapter rings two of these by name, and a label is not a handle: it is localized.
	function hubItem(name, labelKey, onPick) {
		var b = el("button", "obHubItemClass", getRes(labelKey));
		b.type = "button";
		b.setAttribute("role", "menuitem");
		b.setAttribute("data-ob-hub", name);
		b.addEventListener("click", function () {
			if (hubLayer) { hubLayer.close("api"); }
			onPick();
		});
		return b;
	}

	// The hub's one SETTING, as opposed to its three doors: guided MODE on/off
	// (tooltip-engine-consolidation.md §4.9 — the hub is its ONLY switch, owner call
	// 2026-08-09). It changes the app in place and stays where it is, so the menu closes
	// and the state is read fresh on the next open.
	//
	// It was a TICKED row saying "Large tooltips" until 2026-09-01, when the owner made it an
	// ACTION that names its own outcome: four arrows pointing out beside "Make large tooltips"
	// while they are small, four pointing in beside "Make small tooltips" while they are large.
	// The mark is the one a large tooltip wears in its corner (PL.icon), so the corner button
	// and the row that answers it are visibly the same control seen twice — which is what the
	// flight between them is for. A tick could not do that: it states where you are, and this
	// row's job is to offer where you could go.
	function hubTipsSizeRow(isOn, onPick) {
		var b = el("button", "obHubItemClass obHubToggleClass");
		b.type = "button";
		b.setAttribute("role", "menuitem");
		b.setAttribute("data-ob-hub", "guided");
		b.appendChild(el("span", "obHubIconClass"));
		b.appendChild(el("span", "obHubIconLabelClass"));
		paintTipsSizeRow(b, isOn);
		b.addEventListener("click", function () {
			if (hubLayer) { hubLayer.close("api"); }
			onPick();
		});
		return b;
	}

	// Icon + label together — they say one thing between them and must never be set apart.
	function paintTipsSizeRow(row, isOn) {
		var slot = row.querySelector(".obHubIconClass");
		var label = row.querySelector(".obHubIconLabelClass");
		if (!slot || !label) { return; }
		slot.textContent = "";
		slot.appendChild(isOn ? PL.icon.shrink() : PL.icon.expand());
		label.textContent = getRes(isOn ? "tipMakeSmall_label" : "tipMakeLarge_label");
	}

	// …and the same row FLIPPED without rebuilding the menu. The shrink button on a large
	// tooltip opens this menu and then turns the row over in front of the reader (owner spec
	// 2026-09-01), which is the whole point: a menu that simply opened already-flipped would show
	// a state rather than an action. The row is found by handle, not by label.
	function hubFlipTipsSize(on) {
		var row = hubLayer && hubLayer.el.querySelector("[data-ob-hub='guided']");
		if (!row) { return; }
		paintTipsSizeRow(row, on);
		row.classList.add("obHubTickMovedClass");
	}

	/**
	 * opts: { reopen } — close an open menu and build a fresh one instead of toggling shut (the
	 * two callers that OPEN the hub rather than answer a press: the walk's sign-off and the
	 * tooltip's "make it smaller" pill), { unfoldMs } — grow out of `btn` over that long rather
	 * than simply being there (PL.choreo; the same two programmatic callers, since a menu the
	 * READER opened needs no explanation of where it came from), { autoCloseMs } — go by itself
	 * after that long, { keepOnEsc } — Esc is NOT this menu's to answer: it stays open and whatever
	 * is standing on it (the sign-off's popup, which hides itself on Esc through PL.tip's own
	 * window-capture handler) gets the press instead. Without it the two handlers both fire on one
	 * Esc — the popup hides AND PL.layers closes the topmost layer, which is this menu — and the
	 * sequence that was about to fold the menu found it already gone, so Esc and "Got it" ended the
	 * walk in visibly different ways (owner report 2026-09-04). Only the walk's sign-off passes it,
	 * and only because that menu is never the reader's to dismiss: it comes down on its own,
	 * { onPlaced } — handed the menu once it is built and POSITIONED but before
	 * the unfold's transform touches it, which is the only moment a caller can measure a row at
	 * its resting size (the walk's sign-off flies a card into one while the menu is still growing),
	 * { onClosed } — told when this menu instance goes, however it goes,
	 * { markRow } — ring the named row for as long as this menu instance lives, since a
	 * programmatic open is the app pointing at a row rather than the reader's own click landing
	 * on one. Borrows the guide's own asleep/awake pair (`components/onboarding.css`) rather than
	 * a new look: the menu wears `.obAsleepClass` (every OTHER row dims, none of them stop
	 * answering a press — that CSS is deliberately not pointer-events:none) and the named row
	 * keeps `.obAwakeClass`, the one exemption. This is a plain visual borrow, not the wall: no
	 * press-mute, no glow tracker, nothing about the guide chapters runs because of it.
	 */
	function openHub(btn, opts) {
		var o = opts || {};
		if (hubLayer) {
			hubLayer.close("api");
			if (!o.reopen) { return; }   // a second click on the "?" is a toggle shut
		}
		var menu = el("div", "obHubMenuClass");
		menu.setAttribute("role", "menu");
		menu.appendChild(hubItem("replay", "obHelpMenuReplay_label", replay));
		// The panel walk on its own, under the full replay — same subject, one chapter of it.
		// Withheld while a learn or drill screen is up, on the hub's own idiom (the two tip rows
		// are offered the same conditional way): the walk would raise its wall over a question the
		// reader is in the middle of answering, and chapter 3 has no step that belongs on a session
		// screen anyway. It comes back the moment they are out of one.
		if (window.OB_GUIDE && !OB_GUIDE.sessionScreenLive()) {
			menu.appendChild(hubItem("wlTour", "obHelpMenuWlTour_label", startWordListTour));
		}
		menu.appendChild(hubItem("welcome", "obHelpMenuWelcome_label", showWelcomeScreen));
		menu.appendChild(hubTipsSizeRow(PL.mode.guided(), function () {
			PL.mode.setGuided(!PL.mode.guided());
		}));
		// Tip of the day: two doors under a rule, because they are about the language rather than
		// about the app (docs/project/tip-of-the-day.md decision 6). Offered only when there is a
		// bank for this reader's language and pair — tips.js answers that; a menu entry that opens
		// an empty popup teaches nothing.
		if (window.TIPS && TIPS.available()) {
			var group = el("div", "obHubGroupClass");
			group.appendChild(hubItem("tip", "obHelpMenuTip_label", TIPS.showToday));
			group.appendChild(hubItem("allTips", "obHelpMenuAllTips_label", TIPS.showAll));
			menu.appendChild(group);
		}

		if (o.markRow) { markRowIn(menu, o.markRow); }
		var focusToken = PL.focus.remember();
		document.body.appendChild(menu);
		// Under the button, right edges aligned — the "?" sits at the row's right end,
		// so the menu grows leftward and can never leave the viewport.
		var r = btn.getBoundingClientRect();
		menu.style.top = (r.bottom + 6) + "px";
		menu.style.right = Math.max(8, window.innerWidth - r.right) + "px";
		// Placed, at full size, and not yet animating — the one frame in which a row's resting
		// rect can be read (see the doc comment).
		if (o.onPlaced) { o.onPlaced(menu); }
		// …and only now can it grow out of the button: PL.choreo.unfold measures the placed box.
		if (o.unfoldMs && PL.choreo) { PL.choreo.unfold(menu, btn, o.unfoldMs); }

		hubLayer = PL.layers.push({
			el: menu,
			// The opener, plus the walk's two step popups: while the sign-off chapter is talking
			// ABOUT this menu, pressing its "Got it" is not an outside press — the box floats
			// beside the menu and the layer manager would otherwise read it as one and take the
			// menu down mid-sentence. Same contract the add menu has with the same two boxes.
			anchors: [btn].concat(window.OB_GUIDE ? OB_GUIDE.cueAnchors() : []),
			// Returning true = "absorbed, leave me open" (PL.layers). The press is still consumed
			// either way, so nothing behind this menu acts on it.
			onEsc: o.keepOnEsc ? function () { return true; } : null,
			onClose: function () {
				clearTimeout(hubTimer);
				hubTimer = null;
				hubLayer = null;
				menu.remove();
				if (focusToken) { focusToken.restore({ ifWithin: menu }); }
				// …and whoever OPENED this menu as part of a sequence hears that it went, so it
				// can pack up in the same tick rather than at its next beat: a violet row of a
				// menu the reader just clicked away, or an icon still lit for one, is the one thing
				// the sequence must never be caught holding (the shrink pill's HOLD is 4 s long).
				if (o.onClosed) { o.onClosed(); }
			}
		});
		if (o.autoCloseMs) {
			hubTimer = setTimeout(function () {
				hubTimer = null;
				if (hubLayer) { hubLayer.close("api"); }
			}, o.autoCloseMs);
		}
		// Focus goes into the menu only when a PRESS opened it. The two programmatic opens
		// (`reopen`) are things the app is showing rather than things the reader asked for: the
		// walk's sign-off walls the rows anyway, so a focus ring on one is a promise the press
		// mute would not keep, and the shrink pill's menu closes itself a few seconds later.
		var first = o.reopen ? null : menu.querySelector("button");
		if (first) { first.focus(); }
	}

	function closeHub() {
		if (hubLayer) { hubLayer.close("api"); }
	}

	// ── Ringing one row, and letting the ring go ─────────────────────────────────
	//
	// The guide's own asleep/awake pair, borrowed whole (see openHub's doc comment): every
	// other row dims, the named one keeps its face. Split out of openHub because the shrink
	// pill rings its row a BEAT AFTER the menu opens and lets it go a beat before the menu
	// does — the ring is a step of a sequence there, not a property of the menu instance.
	// The dim itself arrives and leaves gradually: components/onboarding.css puts a
	// transition on the rows, so these two functions are the whole of the fade.
	function markRowIn(menu, name) {
		var row = menu && menu.querySelector("[data-ob-hub='" + name + "']");
		if (!row) { return null; }
		menu.classList.add("obAsleepClass");
		row.classList.add("obAwakeClass");
		return row;
	}

	function hubMarkRow(name) {
		return hubLayer ? markRowIn(hubLayer.el, name) : null;
	}

	function hubUnmarkRow() {
		var menu = hubLayer && hubLayer.el;
		if (!menu) { return; }
		menu.classList.remove("obAsleepClass");
		var awake = menu.querySelector(".obAwakeClass");
		if (awake) { awake.classList.remove("obAwakeClass"); }
	}

	// The menu going home: it collapses back INTO the "?" it grew out of, and only then is
	// the layer taken down for real. Presses are cut for the duration — a menu mid-fold is
	// on its way out, and a row it is still drawing must not answer one.
	function foldHub(anchor, duration, done) {
		var menu = hubLayer && hubLayer.el;
		if (!menu || !PL.choreo) {
			closeHub();
			if (done) { done(); }
			return;
		}
		menu.style.pointerEvents = "none";
		PL.choreo.fold(menu, anchor, duration, function () {
			closeHub();
			if (done) { done(); }
		});
	}

	// ── The corner shrink button — a large tooltip's own way out of guided MODE ───
	//
	// Owner spec 2026-09-01. A guided tooltip is a large box that appears on every hover, and
	// until now nothing on it said where it came from: the only switch is a row in the "?" menu,
	// which a reader who has never opened that menu has no reason to look in. So the box carries a
	// quiet square button, and pressing it does not merely flip the setting — it SHOWS the reader
	// where the setting lives, which is the half that matters the second time they want it.
	//
	// ONE GESTURE each way since the owner's 2026-09-04 call, which re-cut this the same way and
	// on the same day as the guided walk's sign-off (js/onboarding_guide.js) — the two are meant
	// to be recognisably the same animation, and they share every number through PL.choreo.
	//
	// It used to be seven beats in a queue, separated by GAPs: a frame flew to the "?", THEN the
	// icon lit, THEN the menu opened, THEN the row rang, THEN the frame climbed down to it. Each
	// step was legible and the whole thing took nine seconds to say one sentence. Now everything
	// that opens starts in the same instant and the beats are told apart by how long they take to
	// FINISH rather than by silences between them:
	//
	//   opening, all at once on the press —
	//     • the tooltip itself flies to the tooltip-size row, visibly shrinking as it goes
	//       (PL.shrinkInto — a "genie" close, the box vanishing INTO the row it is about, not an
	//       empty outline arriving full-size at it);
	//     • the "?" starts turning violet, over the opening's WHOLE length;
	//     • the help menu grows out of the "?" (PL.choreo.unfold);
	//     • the moment the menu has finished arriving — the first frame in which the row is there
	//       to be seen — the row starts lighting up too, and the others dim around it;
	//     • boxFly is UNFOLD + GLOW_FADE exactly so the box lands as both lights reach full: the
	//       flight ends on the thing it was explaining, at the moment that thing is brightest;
	//     • and the row turns over on that same landing — the mode goes off WITH the flip, never
	//       before, because a menu that opened already-flipped would be showing a state rather
	//       than an action.
	//
	//   HOLD (4 s), long enough to have read the row it now says —
	//
	//   closing, all at once —
	//     • the row's violet and the icon's violet both start letting go;
	//     • the menu folds back into the "?" it grew out of, over the same span, so the icon is
	//       plain again exactly as the menu disappears into it.
	//
	// The row's rect is read in `onPlaced` — placed, full size, one frame before the unfold's
	// transform touches it — because that is the only geometry the flight can aim at while the
	// menu is still growing. Same trick, same reason, as the sign-off's.
	//
	// The event is the seam: js/pl/tip.js is a library and knows nothing about this app's help
	// menu, so the box asks the page rather than reaching for it.
	function shrinkBeats() {
		var C = PL.choreo;
		return {
			menuOpen: C.UNFOLD,                  // the menu growing out of the "?"
			rowGlow: C.GLOW_FADE,                // …and the row lighting once it has arrived
			// The two things that finish together, which is the whole shape of the opening: the
			// icon's violet is the ENVELOPE around it (not a beat inside it), and the flight is
			// timed to land on its last frame.
			glowUp: C.UNFOLD + C.GLOW_FADE,
			boxFly: C.UNFOLD + C.GLOW_FADE,
			hold: C.HOLD,
			// …and on the way out, one span for all three: the menu folding, the row letting go,
			// the icon letting go.
			menuClose: C.FOLD,
			glowDown: C.FOLD
		};
	}

	var shrinkRunning = false;

	function shrinkTooltips(box) {
		var btn = document.getElementById("helpBookmarkId");
		if (!btn || !PL.choreo || shrinkRunning) { PL.mode.setGuided(false); return; }
		shrinkRunning = true;
		var B = shrinkBeats();

		// The box, frozen at the size and place it still has THIS instant. js/pl/tip.js dispatches
		// the event while the box is still laid out and hides it on the very next line, so this is
		// the last moment it can be measured — and a CLONE is what flies, leaving the original
		// free to vanish under it. No box (an older tip.js, or one already gone) = no flight: the
		// rest of the sequence plays on its own and still says where the switch lives.
		var snap = (box && document.contains(box) && PL.shrinkInto)
			? { node: box.cloneNode(true), rect: box.getBoundingClientRect() }
			: null;
		// True from the moment the closing asks the menu to fold: the close that follows is the
		// sequence's OWN and must not be mistaken for the reader dismissing it.
		var packing = false;
		// The row wearing the violet, so it can be let go however this ends.
		var lit = null;
		// The press is honoured whatever happens to the sequence: a reader who clicks the page
		// away mid-flight still asked for small tooltips, and the setting is not a beat that can
		// be skipped. Everything else here is explanation, and explanation can be interrupted.
		function stop() {
			shrinkRunning = false;
			if (lit) { rowGlow(lit, false, 0); lit = null; }
			hubUnmarkRow();
			if (PL.mode.guided()) { PL.mode.setGuided(false); }
			fadeHelpGlow(btn);   // …and the icon is never left lit by a sequence that gave up
		}
		function menuGone() {
			if (shrinkRunning && !packing) { stop(); }
		}
		// Everything after the unfold stands on a menu that is still open — the reader was never
		// asked to keep it open, and PL.layers will close it on any outside press.
		function beat(delay, fn) {
			setTimeout(function () {
				if (!shrinkRunning) { return; }
				if (!hubLayer) { stop(); return; }
				fn();
			}, delay);
		}

		// The opening, in one moment: the icon lights, the menu grows out of it, and the box flies
		// into the row that menu is about — none of the three waiting on another.
		function raise() {
			if (!shrinkRunning) { return; }
			var rowRect = null;
			helpGlow(btn, true, B.glowUp);
			openHub(btn, {
				reopen: true,
				unfoldMs: B.menuOpen,
				onClosed: menuGone,
				onPlaced: function (menu) {
					var row = menu.querySelector("[data-ob-hub='guided']");
					rowRect = row ? row.getBoundingClientRect() : null;
				}
			});
			// Into the ROW when there is one to aim at, and into the "?" when the menu could not be
			// measured (a hub that refused to open) — the box still has somewhere to go.
			if (snap) { PL.shrinkInto(snap, rowRect || btn, B.boxFly); }

			// The row, the first frame it is fully there: lit, with the others dimming around it,
			// and still with the box in the air above it.
			beat(B.menuOpen, function () {
				lit = hubMarkRow("guided");
				if (lit) { rowGlow(lit, true, B.rowGlow); }
			});

			// The landing: both violets are at full, and the row turns over into its other face.
			beat(B.boxFly, function () {
				PL.mode.setGuided(false);
				hubFlipTipsSize(false);

				// …and the closing, one gesture again, after the hold.
				beat(B.hold, function () {
					packing = true;
					if (lit) { rowGlow(lit, false, B.menuClose); lit = null; }
					hubUnmarkRow();
					helpGlow(btn, false, B.glowDown);
					foldHub(btn, B.menuClose, function () { shrinkRunning = false; });
				});
			});
		}

		// One tick, and not zero: js/pl/tip.js hides the box in the same tick it dispatched from,
		// and PL.layers takes down everything stacked above a layer it closes — a menu opened
		// alongside would go down with the tooltip. Same moment to the eye.
		setTimeout(raise, 0);
	}

	// The row's violet, arriving and letting go over a span the caller names — the same switch
	// shape as helpGlow below and for the same reason: the class IS the fade, and all this does is
	// say how long it takes. `--ob-row-glow` defaults to 0s in the sheet, so writing it inline is
	// what buys the sequence its slow arrival without slowing a reader's own hover anywhere else.
	function rowGlow(row, on, ms) {
		if (!row) { return; }
		row.style.setProperty("--ob-row-glow", (ms || 0) + "ms");
		row.classList.toggle("obHubLitClass", !!on);
	}

	// The "?"'s violet, lit and let go — one switch, both directions. The violet is an ::after
	// overlay on a transitioned opacity (components/onboarding.css), so the class IS the fade and
	// there is nothing to arrange here beyond HOW LONG it takes: `ms` writes the custom property the
	// transition reads. The walk's sign-off stretches it across its whole opening and its whole
	// closing (owner call 2026-09-04: the glow is the sequence's envelope, starting with the first
	// thing that moves and ending with the last); the shrink pill below asks for the plain
	// PL.choreo.GLOW_FADE, which is also the fallback when no span is named.
	function helpGlow(btn, on, ms) {
		if (!btn) { return; }
		var span = ms || (PL.choreo ? PL.choreo.GLOW_FADE : 700);
		btn.style.setProperty("--ob-help-glow", span + "ms");
		btn.classList.toggle("obHelpLitClass", !!on);
	}

	function fadeHelpGlow(btn) {
		helpGlow(btn, false);
	}

	document.addEventListener("pltipshrink", function (e) {
		shrinkTooltips(e.detail && e.detail.box);
	});

	function wireHelpHub() {
		var btn = document.getElementById("helpBookmarkId");
		if (!btn) { return; }
		// The accessible name stays; the HOVER TOOLTIP is gone (owner call 2026-09-01, both copies
		// of this button). A "?" is the one glyph in the app that needs no gloss, and in guided
		// MODE it was answering a hover with a large box explaining the button whose menu turns
		// large boxes off — the single worst place in the app for one.
		btn.setAttribute("aria-label", getRes("obHelpMenu_label"));
		btn.addEventListener("click", function () { openHub(btn); });
	}

	// ── Boot ──────────────────────────────────────────────────────────────────────

	function parseAttr() {
		if (typeof onboarding_attr !== "string" || !onboarding_attr) { return null; }
		try {
			var parsed = JSON.parse(onboarding_attr);
			return parsed && parsed.state ? parsed : null;
		} catch (e) {
			return null;   // malformed = disarmed, never a broken page
		}
	}

	// ── The tips API — the beacon engine's ONLY door to the attr ──────────────────
	// onboarding_tips.js never writes the `onboarding` attr itself (single-writer rule):
	// seen-ids, the finished-drill count, the distinct-day count and the first-promotion
	// date all live inside this module's `st` and go to the server through its save().
	// Every reader/writer no-ops when st is null (absent attr = pre-tour user, disarmed).
	var tipsApi = {
		armed: function () { return !!st; },
		state: function () { return st ? st.state : null; },
		// The level band the guided run's first words came from ("A2", "B2", "C1"; "A"/"B"/"C"
		// for a rank-banded pair) — stamped by startGuidedRun, null before it or on a run
		// older than the stamp. Read by the guide's "add a few more" step.
		band: function () { return (st && st.band) || null; },
		seen: function (id) { return !!(st && st.tips && st.tips[id]); },
		// Day-of-last-read on the distinct-use-day clock (st.days, ≥1); 0 = never read.
		// The engine wilts a bloom RETIRE_DAYS use-days after this. Values from the
		// first shipped week were date strings — collapse those to day 1 so their
		// blooms wilt on schedule instead of never.
		seenDay: function (id) {
			var v = st && st.tips && st.tips[id];
			if (!v) { return 0; }
			return typeof v === "number" ? v : 1;
		},
		markSeen: function (id) {
			if (!st) { return; }
			if (!st.tips) { st.tips = {}; }
			// EVERY dismissal re-stamps the read day: re-reading a quiet bloom renews its
			// stay — the flower wilts N use-days after the LAST read, not the first.
			st.tips[id] = st.days || 1;
			save();
		},
		drills: function () { return (st && st.drills) || 0; },
		// Finished LEARN sessions (the ladder's recap painting is the signal —
		// learning_session.js finishSession calls learnFinished). The walk's 3-word
		// first scoop lives while learns() has not moved past learnsFrom(): "3 words"
		// is for the FIRST learn session only. Same replay semantics as guideFrom —
		// the counter never resets, only the "since when" mark moves.
		learns: function () { return (st && st.learns) || 0; },
		learnsFrom: function () { return (st && st.learnsFrom) || 0; },
		learnFinished: function (words) {
			if (!st) { return; }
			st.learns = (st.learns || 0) + 1;
			markSessionDone("learn", words);
			save();
		},
		// The drill count at the moment the explore guide was last armed — the glow walk
		// runs while drills() has not moved past it (onboarding_guide.js armed()). Absent
		// on a first visit, which reads as 0 and keeps the original "until the first
		// drill" contract; replay() re-stamps it so an experienced user who asks for the
		// walk again actually gets it.
		guideFrom: function () { return (st && st.guideFrom) || 0; },
		// The TOUR cursor (onboarding_guide.js chapter 2): absent/null = never started,
		// 0..n-1 = the step being taught, -1 = the tour has run its course. The first
		// finished practice drill starts it, a replay clears it.
		tour: function () { return st ? st.tour : null; },
		tourSet: function (i) {
			if (!st || st.tour === i) { return; }
			st.tour = i;
			save();
		},
		// The MEET cursor (onboarding_guide.js chapter 1½ — the tour of a dictionary entry, given
		// on the first flipped meet card of the escorted run). Same three states as `tour`:
		// absent/null = never opened, 0..n-1 = the step being taught, -1 = it has run its course.
		// Once ever (owner call 2026-08-12): the chapter belongs to the first loop, and a replay
		// clears it exactly as it clears the tour.
		meet: function () { return st ? st.meet : null; },
		meetSet: function (i) {
			if (!st || st.meet === i) { return; }
			st.meet = i;
			save();
		},
		// The WORD LISTS cursor (onboarding_guide.js chapter 3 — the walk through the panel,
		// offered by tourContinueCard once the dictionary chapter ends). Same three states as
		// the two above, with one addition of meaning: -1 covers BOTH "walked it" and "declined
		// the offer", because neither is asked again. Cleared by a replay like the others.
		wl: function () { return st ? st.wl : null; },
		wlSet: function (i) {
			if (!st || st.wl === i) { return; }
			st.wl = i;
			save();
		},
		// The SIGN-OFF cursor (onboarding_guide.js chapter 4 — the two last words, said in the
		// open help menu on whichever way the walk ended). null = never said, 0..n-1 = the step
		// being read, -1 = said. Cleared by a replay like the others.
		sign: function () { return st ? st.sign : null; },
		signSet: function (i) {
			if (!st || st.sign === i) { return; }
			st.sign = i;
			save();
		},
		// How the walk was LEFT EARLY (owner call 2026-10-05): every ending writes the same -1
		// cursors, so without this a walk run to the end and one quit at its second step read
		// identically. `how` = "quit" (the quit card) | "declinedWl" (the Word Lists offer
		// declined) | "dict" (the Dictionary door, first-run R16); the cursors are the ones live at
		// the moment of leaving, taken before endTour resets them. Absent = never left early.
		// Cleared by a replay like the cursors.
		//
		// Leaving the guided RUN early is also an ANSWER (owner ruling R23,
		// docs/project/first-run-redesign.md): `running` becomes `skipped`, the state the welcome
		// card's own decline writes. Left at `running`, every later page load raised the welcome
		// card again (the boot rule below) — and pressing it then staged the words with no walk at
		// all, every cursor already being at its end. The "?" menu's Guided tour row is the way back
		// in (replay), which is what the Dictionary card promises. The console's tour column reads
		// `ended.how` before the state, so a quit still reads as a quit there.
		endedEarly: function (how) {
			if (!st) { return; }
			st.ended = { how: how, ts: today(), meet: st.meet, tour: st.tour, wl: st.wl };
			if (st.state === "running") {
				st.state = "skipped";
				st.skipped_ts = today();
			}
			save();
		},
		// The walk run to its END (owner call 2026-10-05) — the twin of `ended`: chapter 3 walked
		// off its last step. `via` = "tour" (reached through the whole walk) | "hub" (chapter 3
		// opened on its own from the Help hub). Absent = never walked to the end. Cleared by a
		// replay like the cursors.
		walkFinished: function (via) {
			if (!st) { return; }
			st.finished = { ts: today(), via: via };
			save();
		},
		promoted: function () { return !!(st && st.promo); },
		// One call per finished drill session (main.js's session-end hook, via OB_TIPS).
		drillFinished: function (promotedNow, words) {
			if (!st) { return; }
			st.drills = (st.drills || 0) + 1;
			if (promotedNow && !st.promo) { st.promo = today(); }
			markSessionDone("drill", words);
			save();
		},
		days: function () { return (st && st.days) || 0; },
		// Counts DISTINCT calendar days the app was opened ("after the 3rd session" gates
		// read this — reloads within a day don't inflate it). Called once per boot.
		touchDay: function () {
			if (!st || st.lastDay === today()) { return; }
			st.lastDay = today();
			st.days = (st.days || 0) + 1;
			save();
		}
	};

	ONBOARDING = {
		finishScreenShown: finishScreenShown, tips: tipsApi, replay: replay,
		tourQuitCard: tourQuitCard, tourContinueCard: tourContinueCard, tourDoorCard: tourDoorCard,
		// Is the guided run live RIGHT NOW? `running` is set the moment the welcome card's
		// "meet the words" is pressed and cleared by finishScreenShown above, so this is true
		// for exactly the drill the tour staged — the window in which the spec's "the 3-word
		// onboarding tour alone runs showings=1" applies (garden-srs-redesign-spec.md §2
		// slider bullet). training_session.js's getRepetition() is the only caller; the tour
		// stages its words through that SHARED drill and owns no showings control of its own,
		// so this predicate is the whole of what tells the drill it is running the tour.
		// A disarmed module (st === null: every user predating the tour) answers false, which
		// is the correct answer — those users cannot be inside a guided run.
		guidedRunLive: function () { return !!(st && st.state === "running"); },
		// The hub, opened from a "?" that is NOT the tabs row's — the finish card grows its own
		// pair of pills because the tabs row is hidden behind it (main.js exFinishPills). The menu
		// hangs off whatever button is passed, so a second door needs no second menu.
		// See openHub for the option set the walk's sign-off and the shrink pill pass.
		openHelpHub: openHub,
		closeHelpHub: closeHub,
		// The icon's violet, lit or let go over a span the caller names — the walk's sign-off is
		// the other caller (js/onboarding_guide.js steps 2 and 6).
		helpGlow: helpGlow,
		// …and the menu going home the way it came: collapsing back into the button it grew out
		// of, before the layer is taken down. The walk's sign-off (step 5) is the other caller.
		foldHelpHub: foldHub
	};

	$(function () {
		// Dev trigger (spec §5): ?tour=reset re-arms the tour for this user and cleans the URL.
		if (/[?&]tour=reset\b/.test(window.location.search)) {
			st = { v: 1, state: "fresh" };
			save();
			if (window.history && history.replaceState) {
				history.replaceState(null, "", window.location.pathname);
			}
		} else {
			st = parseAttr();
		}
		wireHelpHub();         // for everyone — the hub is also the pre-tour user's way in
		// The guest app page (/<locale>/<learn>/app): there is no user and therefore no attr, so
		// everything below would stand down — and the card is precisely what that page is for.
		// No 350 ms defer here: the card is already painted (main.html), this only fills it.
		if (window.guest_mode) {
			showGuestWelcome();
			return;
		}
		if (!st) { return; }   // absent attr: existing user, nothing shows unbidden
		tipsApi.touchDay();    // day-of-use counter for the milestone-gated tips (§6)
		if (st.state === "fresh" || st.state === "offered" || st.state === "running") {
			// Small defer: let the app's own ready handlers paint first — the card overlays a
			// live page, not a blank one (spec §12.2: the app stays visible behind it).
			setTimeout(showWelcome, 350);
		}
	});

})();
